import Alert from '@/components/Alert';
import Button from '@/components/Button';
import {
  AUTH_EMAIL_EXPECTED_SENDER,
  AUTH_EMAIL_SPAM_HELP,
} from '@/lib/authEmail';

export const EMAIL_NOT_VERIFIED_ERROR = 'User email not verified.';
export const RESEND_FAILURE_MESSAGE =
  'We could not resend the verification email right now. Please try again in a few moments.';
export const RESEND_SUCCESS_MESSAGE =
  "We've sent another email if this address can receive verification messages.";
const RESEND_HELPER_MESSAGE = AUTH_EMAIL_SPAM_HELP;

export type ResendStatus =
  | { kind: 'idle' }
  | { kind: 'success'; message: string }
  | { kind: 'cooldown'; message: string; retryAfterSeconds: number }
  | { kind: 'error'; message: string };

const formatRetryAfter = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

type Props = {
  email: string;
  status: ResendStatus;
  cooldownSecondsRemaining: number | null;
  isLoading: boolean;
  onResend: () => void;
};

const ResendVerificationCallout = ({
  email,
  status,
  cooldownSecondsRemaining,
  isLoading,
  onResend,
}: Props) => {
  const isCooldownActive =
    status.kind === 'cooldown' && (cooldownSecondsRemaining ?? 0) > 0;
  const resendButtonLabel = isCooldownActive
    ? `Try again in ${formatRetryAfter(cooldownSecondsRemaining ?? 0)}`
    : 'Resend verification email';

  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-900">
      <div className="space-y-3">
        <div className="space-y-1">
          <p className="font-medium">Need another verification email?</p>
          <p className="text-gray-700">
            We&apos;ll send it to {email}. {RESEND_HELPER_MESSAGE}
          </p>
          <p className="text-gray-700">
            Expected sender: {AUTH_EMAIL_EXPECTED_SENDER}
          </p>
        </div>
        <Button
          type="button"
          color="outline"
          size="sm"
          className="w-full"
          onClick={onResend}
          loading={isLoading}
          disabled={isCooldownActive}
        >
          {resendButtonLabel}
        </Button>
        {status.kind === 'success' && (
          <Alert
            variant="success"
            message={`${status.message} ${RESEND_HELPER_MESSAGE}`}
          />
        )}
        {isCooldownActive && (
          <Alert
            variant="info"
            message={`Please wait ${formatRetryAfter(
              cooldownSecondsRemaining ?? 0
            )} before requesting another verification email. ${RESEND_HELPER_MESSAGE}`}
          />
        )}
        {status.kind === 'error' && (
          <Alert variant="error" message={status.message} />
        )}
      </div>
    </div>
  );
};

export default ResendVerificationCallout;
